/* ==========================================================================
   Optimize Minerals — Add to cart price

   The button states a total, and two controls decide it: the pack selector
   (how many) and Skio's plan picker (subscription or one time). On its own the
   button showed the variant's unit price, so a 2-pack read the same as a
   1-pack, and only the plan moved it.

   The price under the title is driven from the same pair, and for the same
   reason. It moved for the plan - Skio rewrites it - but nothing ever told it
   about the pack, so it sat on the single-unit price whatever was selected:
   $55 above a button reading $198. It now follows the button.

   Both totals are precomputed in Liquid and sit on each pack radio as
   data-atc-onetime / data-atc-subscription. Nothing is calculated or formatted
   here: the arithmetic is Shopify's and the money format is the theme's, which
   is what a button that quotes a price deserves. This file only decides which
   of the labels is the true one right now and puts it everywhere.

   Skio writes its own price into two of these elements when the plan changes.
   Rather than fight it, that write is the signal to recompute - see the
   observers at the bottom.

   Every write is idempotent: write() returns early when the element already
   reads what it would say. That is what lets the observers run on EVERY
   mutation, including our own, with no flag to say whose write it was. An
   earlier version held a WRITING flag across the write and cleared it in a
   timeout, and Skio's own load-time write landed inside that window - one
   observer callback carried both records, the flag was still up, and the
   correction was skipped. The button then sat on Skio's unit price for good:
   a 2-pack that read $55 rather than $104, with nothing left to mutate and
   trigger a retry.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Writing

     The rate guard counts WRITES, not calls. apply() runs on every mutation
     anywhere near a price, and nearly all of those find everything already
     correct and change nothing - counting those would exhaust the budget on
     traffic that is not ours. A pathological ping-pong (some other script
     rewriting a price in response to ours) is what this is for: Skio does not
     do this, but this is a live storefront and a runaway observer loop would
     lock the tab.
     ------------------------------------------------------------------ */
  var writes = 0;
  var since = 0;

  function write(node, text) {
    if (!node || node.textContent === text) return false;

    var now = Date.now();
    if (now - since > 1000) { since = now; writes = 0; }
    if (++writes > 60) return false;

    node.textContent = text;
    return true;
  }

  /* ------------------------------------------------------------------
     What is selected
     ------------------------------------------------------------------ */

  function priceEl() {
    return document.querySelector('[id^="variant-price-"]');
  }

  function checkedPack() {
    return document.querySelector('.op-pack__radio:checked');
  }

  /* Empty selling_plan means one time. The input is Skio's, in the light DOM,
     tied to the product form by its `form` attribute. */
  function isSubscription() {
    var input = document.querySelector('skio-plan-picker input[name="selling_plan"]');
    return !!(input && input.value);
  }

  /* ------------------------------------------------------------------
     The price under the title

     Shopify's price snippet draws two versions of the same figure and shows
     one: .price__sale when the variant carries a compare_at, .price__regular
     otherwise. Only the visible one is written. The hidden one holds a stale
     copy either way, and writing it would put us in the way of Skio, which
     rewrites every .price-item--regular on the page whenever the plan changes.
     ------------------------------------------------------------------ */
  function titlePrice(now, was) {
    var root = document.querySelector('.price.price--large');
    if (!root) return;

    var wrap = root.querySelector(
      root.classList.contains('price--on-sale') ? '.price__sale' : '.price__regular'
    );
    if (!wrap) return;

    write(wrap.querySelector('.price-item--regular'), now);

    /* Skio appends this element itself when a plan is selected and removes it
       when one is not, and it finds ours by the same selector it would use for
       its own - so the two never both exist, and its removal is undone on the
       next pass with the right figure in it. Built here when it is absent
       because a one-time purchase of a discounted pack has something to strike
       out and Skio, which only ever compares plans, does not know it. */
    var s = wrap.querySelector('.price-item--sale');
    if (!was) {
      if (s) write(s, '');
      return;
    }
    if (!s) {
      var span = document.createElement('span');
      s = document.createElement('s');
      s.className = 'price-item price-item--sale';
      span.appendChild(s);
      wrap.appendChild(span);
    }
    write(s, was);
  }

  /* ------------------------------------------------------------------
     One pass
     ------------------------------------------------------------------ */
  /* No pack selector on this product.

     Everything below is driven from totals precomputed onto the pack radios,
     so with no packs there is nothing to read and this used to return at the
     first line - which was right for the price (Skio and Liquid already put
     the correct figure on the button) but wrong for the struck one. Nobody
     else writes that element, so it stayed empty and collapsed, and a product
     like Hydration showed "$55" on a button above a title reading "$55 $69".

     So the struck figure is mirrored from the price under the title, which is
     the same claim about the same variant and is already kept current by Skio
     across plan changes. Read-only: the pack path writes to that block through
     titlePrice(), this one never does, so there is no way for the two to chase
     each other. */
  function strikeFromTitlePrice() {
    var root = document.querySelector('.price.price--large');
    if (!root) return;

    /* Same choice of container as titlePrice(), for the same reason: Shopify
       renders both and shows one. */
    var wrap = root.querySelector(
      root.classList.contains('price--on-sale') ? '.price__sale' : '.price__regular'
    );
    if (!wrap) return;

    var now = wrap.querySelector('.price-item--regular');
    var s = wrap.querySelector('.price-item--sale');
    var was = s ? s.textContent.trim() : '';

    /* The hidden half of the price snippet holds a bare currency symbol where
       the compare-at would go. A symbol on its own is an empty slot, not a
       price, and striking it out on the button would read as a mistake. */
    if (!/\d/.test(was)) was = '';
    if (now && was === now.textContent.trim()) was = '';

    document.querySelectorAll('.js-atc-price-was').forEach(function (el) {
      write(el, was);
    });
  }

  /* The per-serving line on a product with no pack selector.

     The pack path below owns this line where a pack selector exists, because
     the figure then depends on which pack is chosen. The single products -
     Hydration, Omegas, Vitaminz - have no tiles to hang it on, so it comes
     from the section setting instead, rendered onto the element itself in
     main-product.liquid. Same division of labour as everywhere else in this
     file: Liquid did the arithmetic and the formatting, this only picks which
     of the two figures is true right now. */
  function servingFromSection(sub) {
    document.querySelectorAll('.js-price-serving').forEach(function (el) {
      var serving = el.getAttribute(sub ? 'data-serving-subscription' : 'data-serving-onetime');
      write(el, serving ? serving + ' per serving' : '');
    });
  }

  /* ------------------------------------------------------------------
     The sticky bar's second line

     It has to read as the CURRENT state of the buy box, not as the variant
     title. Two things were wrong with the title: it led with the supply size
     ("30 Day Supply / Mixed Berry"), which the bar already implies through the
     price, and it said nothing about the choice that actually changes what is
     charged - subscribe or one time.

     So the size is dropped and the plan takes its place. Everything else the
     shopper picked (flavour, and anything else that is not Size) follows it,
     in the order the pickers appear.

     Read from the checked radios rather than from a variant object, because
     this runs on plan changes too, when no variant event has fired.
     ------------------------------------------------------------------ */
  function stickyVariantLine() {
    var el = document.querySelector('.js-sticky-variant');
    if (!el) return;

    var parts = [];
    document.querySelectorAll('.product-form__input[data-option-name]').forEach(function (fs) {
      var name = (fs.getAttribute('data-option-name') || '').toLowerCase();
      if (name === 'size') return;
      var checked = fs.querySelector('input[type="radio"]:checked');
      if (checked && checked.value) parts.push(checked.value);
    });

    var mode = isSubscription() ? 'Subscribe and save' : 'One time purchase';
    write(el, parts.length ? mode + ' / ' + parts.join(' / ') : mode);
  }

  /* global.js calls this from its own variant-change handler, so the line is
     rewritten by whichever of the two moves first. */
  window.opStickyVariantLine = stickyVariantLine;

  /* ------------------------------------------------------------------
     The sticky price on a product with no pack selector

     apply() below returns early when there is no checked pack, so on those
     products nothing ever wrote .js-sticky-price and it kept the figure Liquid
     rendered: the variant's own price. That ignored the plan AND the variant,
     so the bar sat at $81 under a button reading $79 the moment Subscribe was
     chosen - the bundle in the client's screenshot.

     The button's price is the live one. Skio rewrites it when the plan
     changes, and it is priceEl(), which watch() already observes - so that
     rewrite brings us back here and the bar follows in the same pass. Mirroring
     it is also the only way the two are guaranteed to agree: a second
     calculation is a second thing to drift.
     ------------------------------------------------------------------ */
  function stickyFromButton() {
    var sticky = document.querySelector('.js-sticky-price');
    var btn = priceEl();
    if (!sticky || !btn) return;
    var text = (btn.textContent || '').replace(/\s+/g, ' ').trim();
    if (text) write(sticky, text);
  }

  function apply() {
    /* First, and outside every early return below: the bar's line must track
       the plan even on a product with no pack selector. */
    stickyVariantLine();

    var pack = checkedPack();
    if (!pack) {
      strikeFromTitlePrice();
      servingFromSection(isSubscription());
      stickyFromButton();
      return;
    }

    var sub = isSubscription();
    var now = pack.getAttribute(sub ? 'data-atc-subscription' : 'data-atc-onetime');
    if (!now) return;

    /* Blanked when there is nothing to compare against, and blanked again if
       the comparison ever equals the price: a button reading "$69 $69" is worse
       than one reading "$69". Emptying it also collapses it, since these
       elements are hidden while empty. */
    var was = pack.getAttribute(sub ? 'data-atc-subscription-was' : 'data-atc-onetime-was') || '';
    if (was === now) was = '';

    write(priceEl(), now);
    document.querySelectorAll('.js-sticky-price').forEach(function (el) {
      write(el, now);
    });
    /* The struck figure is written in the same pass as the price, never in an
       observer of its own, so it can never lag the price it qualifies - the two
       are one statement and they move together. */
    document.querySelectorAll('.js-atc-price-was').forEach(function (el) {
      write(el, was);
    });

    /* The price under the title states the price of ONE, not of the pack.

       The total belongs in exactly one place - the Add to cart button, where it
       is the amount about to be charged. Everything else on this page answers
       "what does a canister cost on this option", and when the title price
       answered the other question it competed with the button instead of
       supporting it: a heading reading $220.80 over tiles reading $55.20 each
       looked like two different products rather than one priced two ways.

       Falls back to the total if a page predates the per-unit attributes, for
       the same reason the labels do. */
    var unitNow = pack.getAttribute(sub ? 'data-pack-each-subscription' : 'data-pack-each-onetime') || now;
    var unitWas = pack.getAttribute(sub ? 'data-pack-each-subscription-was' : 'data-pack-each-onetime-was') || '';
    if (unitWas === unitNow) unitWas = '';

    titlePrice(unitNow, unitWas);

    /* "each" qualifies the figure under the title, which is a per-canister
       price. Without it a lone "$55" above a button reading "$220" invites the
       reading that one of the two is wrong. */
    titleEach(unitNow ? 'each' : '');

    /* And under that, the same money expressed per serving - the unit a
       customer actually experiences. Empty when the block has no servings
       count, which collapses the line. */
    var serving = pack.getAttribute(sub ? 'data-pack-serving-subscription' : 'data-pack-serving-onetime');
    document.querySelectorAll('.js-price-serving').forEach(function (el) {
      write(el, serving ? serving + ' per serving' : '');
    });

    packTiles(sub);
  }

  /* The word sits INSIDE the price container, after the struck figure, so it
     rides the same baseline. It has to be placed from here rather than rendered
     in Liquid: the struck figure is appended at run time, so anything written
     into the markup ahead of time would end up in front of it. */
  function titleEach(text) {
    var root = document.querySelector('.price.price--large');
    if (!root) return;
    var wrap = root.querySelector(
      root.classList.contains('price--on-sale') ? '.price__sale' : '.price__regular'
    );
    if (!wrap) return;

    var el = wrap.querySelector('.op-price-each');
    if (!el) {
      if (!text) return;
      el = document.createElement('span');
      el.className = 'op-price-each';
      wrap.appendChild(el);
    }
    /* Always last, so a struck figure appended after it cannot jump ahead. */
    if (el.nextSibling) wrap.appendChild(el);
    write(el, text);
  }

  /* ------------------------------------------------------------------
     The pack tiles

     EVERY tile, not only the checked one. The row exists to be compared down -
     $55, $46.75, $44 - and a comparison is only honest if all three are quoting
     the same mode at the same moment.

     They are also the reason this function exists at all. The tiles are
     rendered from Liquid, which cannot know which plan Skio will select, so
     they ship with the one-time figure; the plan defaults to subscribe, and the
     row sat at "$69 each" under a button reading "$55" from the moment the page
     loaded. Same failure the button and the title price had, in a third place.
     ------------------------------------------------------------------ */
  function packTiles(sub) {
    document.querySelectorAll('.op-pack__radio').forEach(function (radio) {
      var label = radio.getAttribute(sub ? 'data-pack-each-subscription' : 'data-pack-each-onetime');
      if (!label) return;
      var tile = radio.nextElementSibling;
      if (!tile) return;
      write(tile.querySelector('.js-pack-each'), label);
    });
  }

  /* ------------------------------------------------------------------
     When to run
     ------------------------------------------------------------------ */

  /* A pack was chosen. */
  document.addEventListener('change', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('op-pack__radio')) apply();
  });

  /* The plan was chosen.

     This is the trigger, and watching Skio's writes is only the backstop. The
     observer below catches the plan change when Skio happens to rewrite one of
     the elements it watches, and that turned out not to be dependable: choosing
     One time left every figure on the page showing subscription prices, and a
     later pack click was the only thing that put them right.

     A click is composed, so one that starts inside Skio's shadow root still
     reaches document - retargeted to the <skio-plan-picker> host, which is
     exactly what closest() needs to see. Capture phase, so it runs even if
     something inside stops the bubble. `change` as well, for the keyboard.

     Both go through applyAndSettle rather than apply, because Skio has not
     necessarily updated its own state by the time the click lands - the point
     of the later passes is to read it once it has. */
  function planChanged(e) {
    var t = e.target;
    if (t && t.closest && t.closest('skio-plan-picker')) applyAndSettle();
  }
  document.addEventListener('click', planChanged, true);
  document.addEventListener('change', planChanged, true);

  /* The plan changed. Skio's radios live in a shadow root and `change` does not
     cross that boundary, so the plan is not observable directly - but Skio
     rewrites the button price and the title price when it changes, and those
     are observable. Its write is the trigger; ours end in write()'s early
     return and stop there. */
  /* Once now, and again once Skio has settled.

     Skio writes its price into these elements BEFORE it clears the
     selling_plan input, so the mutation that wakes us arrives while the input
     still holds the old plan: switching to one time repainted every figure with
     the SUBSCRIPTION labels, and nothing mutated afterwards to trigger a
     correction. The page sat on $55 each under a one-time button until the next
     pack click.

     The input's `value` is a property, not an attribute, so there is nothing to
     observe on it directly and no event of Skio's to bind to. Re-running
     shortly after is what closes the gap. Two passes rather than one because a
     slow device can stretch that gap; both are free when nothing has changed,
     since every write is idempotent and the budget only counts real writes. */
  var settleTimers = [];
  function applyAndSettle() {
    apply();
    settleTimers.forEach(clearTimeout);
    settleTimers = [setTimeout(apply, 60), setTimeout(apply, 300), setTimeout(apply, 800)];
  }

  function observe(el) {
    if (!el || el.dataset.opAtcWatched === '1') return;
    el.dataset.opAtcWatched = '1';
    new MutationObserver(applyAndSettle).observe(el, {
      childList: true, characterData: true, subtree: true
    });
  }

  function watch() {
    observe(priceEl());
    observe(document.querySelector('.price.price--large'));
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
  /* The buy column is re-rendered on a variant change, which replaces the
     button, the pack radios and the price under the title with fresh markup and
     new totals. */
  document.addEventListener('shopify:section:load', watch);
  new MutationObserver(watch).observe(document.documentElement, { childList: true, subtree: true });
})();